/**
 * Rutas — TanStack Router en modo código.
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { AppShell } from '@/components/app-shell'
import { bootStage, SessionProvider, useSession } from '@/lib/session'
import { SetupPage } from '@/routes/setup'
import { LoginPage } from '@/routes/login'
import { MorePage } from '@/routes/more'
import { FleetPage } from '@/routes/fleet'
import { FleetNewPage } from '@/routes/fleet-new'
import { FleetDetailPage } from '@/routes/fleet-detail'
import { FleetEditPage } from '@/routes/fleet-edit'
import { InspectionsPage } from '@/routes/inspections'
import { InspectionNewPage } from '@/routes/inspection-new'
import { InspectionDetailPage } from '@/routes/inspection-detail'
import { WorkOrdersPage } from '@/routes/work-orders'
import { WorkOrderNewPage } from '@/routes/work-order-new'
import { WorkOrderDetailPage } from '@/routes/work-order-detail'
import { VendorsPage } from '@/routes/vendors'
import { VendorNewPage } from '@/routes/vendor-new'
import { VendorEditPage } from '@/routes/vendor-edit'
import { ExpensesPage } from '@/routes/expenses'
import { MaintenancePage } from '@/routes/maintenance'
import { DashboardPage } from '@/routes/dashboard'
import { ReportsPage } from '@/routes/reports'
import { SettingsPage } from '@/routes/settings'

function BootGate() {
  const { status, loading, error } = useSession()
  const stage = bootStage(status, loading)

  if (stage === 'loading') {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <p className="text-sm text-muted-foreground">Cargando…</p>
      </div>
    )
  }

  if (error && !status) {
    return (
      <div className="mx-auto grid min-h-screen max-w-md place-items-center px-4">
        <div className="text-center">
          <h1 className="text-lg font-semibold">No se pudo contactar con la API</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <p className="mt-4 text-sm text-muted-foreground">
            En desarrollo, comprueba que <code className="font-mono text-xs">npm run dev</code> haya
            levantado también el servidor de API en el puerto 5174.
          </p>
        </div>
      </div>
    )
  }

  if (stage === 'needs-setup') return <SetupPage />
  if (stage === 'needs-login') return <LoginPage />

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  )
}

const rootRoute = createRootRoute({
  component: () => (
    <SessionProvider>
      <BootGate />
    </SessionProvider>
  ),
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' })
  },
})

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: DashboardPage,
})

const fleetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fleet',
  component: FleetPage,
})

const fleetNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fleet/new',
  component: FleetNewPage,
})

const fleetDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fleet/$vehicleId',
  component: FleetDetailPage,
})

// OJO al orden con '/fleet/$vehicleId': TanStack Router da prioridad a los
// segmentos estáticos, así que '/fleet/new' y '/fleet/:id/edit' se resuelven
// bien aunque el parámetro los solape.
const fleetEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fleet/$vehicleId/edit',
  component: FleetEditPage,
})

const inspectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inspections',
  component: InspectionsPage,
})

const inspectionNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inspections/new',
  component: InspectionNewPage,
})

const inspectionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inspections/$inspectionId',
  component: InspectionDetailPage,
})

const workOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work-orders',
  component: WorkOrdersPage,
})

const workOrderNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work-orders/new',
  component: WorkOrderNewPage,
})

const workOrderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work-orders/$workOrderId',
  component: WorkOrderDetailPage,
})

const vendorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vendors',
  component: VendorsPage,
})

const vendorNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vendors/new',
  component: VendorNewPage,
})

const vendorEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vendors/$vendorId/edit',
  component: VendorEditPage,
})

const expensesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/expenses',
  component: ExpensesPage,
})

const maintenanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/maintenance',
  component: MaintenancePage,
})

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  component: ReportsPage,
})

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsPage,
})

const moreRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/more',
  component: MorePage,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  dashboardRoute,
  fleetRoute,
  fleetNewRoute,
  fleetDetailRoute,
  fleetEditRoute,
  inspectionsRoute,
  inspectionNewRoute,
  inspectionDetailRoute,
  workOrdersRoute,
  workOrderNewRoute,
  workOrderDetailRoute,
  vendorsRoute,
  vendorNewRoute,
  vendorEditRoute,
  expensesRoute,
  maintenanceRoute,
  reportsRoute,
  settingsRoute,
  moreRoute,
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
